import { Icons } from "assets";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import CustomButton from "shared/components/common/customButton";
import CustomPagination from "shared/components/common/customPagination";
import { toastMessage } from "shared/components/common/toast";
import ReviewStep from "shared/components/questionnaire/review";
import QuestionnaireStep1 from "shared/components/questionnaire/step1";
import QuestionnaireStep10 from "shared/components/questionnaire/step10";
import QuestionnaireStep2 from "shared/components/questionnaire/step2";
import QuestionnaireStep3 from "shared/components/questionnaire/step3";
import QuestionnaireStep4 from "shared/components/questionnaire/step4";
import QuestionnaireStep5 from "shared/components/questionnaire/step5";
import QuestionnaireStep6 from "shared/components/questionnaire/step6";
import QuestionnaireStep7 from "shared/components/questionnaire/step7";
import QuestionnaireStep8 from "shared/components/questionnaire/step8";
import QuestionnaireStep9 from "shared/components/questionnaire/step9";
import { setBreadCrumb } from "shared/redux/reducers/breadCrumbSlice";
import { routeConstant } from "shared/routes/routeConstant";
import {
  GetCurrentEvaluationVersionDetailService,
  UpdateEvaluationService,
} from "shared/services/evaluations";
import { JobRole } from "shared/utils/enum";
import {
  checkDropdownOptions,
  checkIfWarning,
  mergeArrays,
} from "shared/utils/helper";
import styles from "./style.module.scss";
import {
  GetAllDropdownsWithOptionsService,
  GetAppOutcomesService,
  GetQuestionsService,
} from "shared/services/questionnaire";
import { setQuestionsReducer } from "shared/redux/reducers/questions";
import ConfirmationModal from "shared/modal/confirmation";

const EditQuestionnaire = () => {
  const { id, org } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const formikRef = useRef<any>(null);
  const finalPayload = useRef<any>({});
  const loadingRef = useRef<boolean>(false);
  const parentId = useRef<number>(0);
  const initialFormData = useRef<any[]>([]);

  const [step, setStep] = useState<number>(1);
  const [formData, setFormData] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [evalsLoading, setEvalsLoading] = useState<boolean>(true);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);

  const handleShowConfirmationModal = () => {
    setShowConfirmationModal(true);
  };

  const handleCloseConfirmationModal = () => {
    setShowConfirmationModal(false);
  };

  const handleGetQuestions = () => {
    GetQuestionsService()
      .then(({ data }) => {
        dispatch(setQuestionsReducer({ questions: data }));
      })
      .catch((err) => {})
      .finally(() => {});
  };

  const handleGetEvaluations = async () => {
    setEvalsLoading(true);
    if (id) {
      GetCurrentEvaluationVersionDetailService({
        id: String(id),
      })
        .then(async ({ data: { data } }) => {
          let response = null;
          if (data?.roleType === JobRole.management) {
            response = await GetAppOutcomesService({
              level: data?.q6?.level,
              outcome: data?.q8?.level,
            });
            setEvalsLoading(false);
          } else {
            setEvalsLoading(false);
          }
          let tempObj: any = {
            jobTitle: data?.jobTitle,
            department: data?.department,
            roleType: {
              name:
                data?.roleType === JobRole.management
                  ? "Manager"
                  : "Individual Contributor",
              value: data?.roleType,
            },
            q1: {
              jobTitle: data?.jobTitle,
              levelWork: {
                name: data?.level_of_work?.short_des,
                value: data?.level_of_work?.id,
              },
              modifier: {
                name: data?.modifier,
                value: data?.modifier,
              },
              definition: data?.level_of_work?.long_des,
              alldropDowns: [],
              output: data?.modifierScore ? data?.modifierScore : 0,
            },
            q2: {
              jobTitle: data?.jobTitle,
              institutionalExpertise: {
                name: data?.q2?.short_des,
                value: data?.q2?.id,
                level: data?.q2?.level,
              },
              definition: data?.q2?.long_des,
              output: data?.q2?.score ? data?.q2?.score : 0,
            },
            q3: {
              jobTitle: data?.jobTitle,
              culturalImpact: {
                name: data?.q3?.short_des,
                value: data?.q3?.id,
                level: data?.q3?.level,
              },
              definition: data?.q3?.long_des,
              output: data?.q3?.score ? data?.q3?.score : 0,
            },
            q4: {
              jobTitle: data?.jobTitle,
              physicalEnvironment: {
                name: data?.q4?.short_des,
                value: data?.q4?.id,
                level: data?.q4?.level,
              },
              definition: data?.q4?.long_des,
              output: data?.q4?.score ? data?.q4?.score : 0,
            },
            q5: {
              jobTitle: data?.jobTitle,
              mentalEnvironment: {
                name: data?.q5?.short_des,
                value: data?.q5?.id,
                level: data?.q5?.level,
              },
              definition: data?.q5?.long_des,
              output: data?.q5?.score ? data?.q5?.score : 0,
            },
            q6: {
              jobTitle: data?.jobTitle,
              applicationLevel: {
                name: data?.q6?.short_des,
                value: data?.q6?.id,
                level: data?.q6?.level,
              },
              definition: data?.q6?.long_des,
              output: data?.q6?.score ? data?.q6?.score : 0,
            },
            q7: {
              jobTitle: data?.jobTitle,
              manualEffort: {
                name: data?.q7?.short_des,
                value: data?.q7?.id,
                level: data?.q7?.level,
              },
              definition: data?.q7?.long_des,
              output: data?.q7?.score ? data?.q7?.score : 0,
            },
            q8: {
              jobTitle: data?.jobTitle,
              outcomes: {
                name: data?.q8?.short_des,
                value: data?.q8?.id,
                level: data?.q8?.level,
              },
              definition: data?.q8?.long_des,
              output:
                data?.roleType === JobRole.management
                  ? response?.data?.outcome
                  : data?.q8?.score
                  ? data?.q8?.score
                  : 0,
            },
            q9: {
              jobTitle: data?.jobTitle,
              notes: data?.q9 ? data?.q9 : "",
            },
            shortCode: data?.shortCode,
            rasScore: data?.rasScore,
            rasLevel: data?.rasLevel,
            jobCode: data?.jobeCode ? data?.jobeCode : "",
            jobGrade: data?.jobGrade ? data?.jobGrade : "",
          };
          if (data?.modifier && data?.level_of_work) {
            let response = await GetAllDropdownsWithOptionsService({
              level_id: data?.level_of_work?.id,
              modifier: data?.modifier,
            });
            tempObj.q1.alldropDowns = response?.data?.score_options;
          }
          initialFormData.current = [tempObj];
          setFormData([tempObj]);
          parentId.current = data?.parent;
        })
        .catch((err) => {
          navigate(-1);
          toastMessage("error", err?.response?.data?.error);
          setEvalsLoading(false);
        });
    } else {
      navigate(-1);
    }
  };

  const handleSubmission = () => {
    setLoading(true);
    UpdateEvaluationService(
      finalPayload.current?.evals[0],
      String(parentId.current)
    )
      .then(() => {
        toastMessage("success", "Evaluation updated successfully");
        navigate(
          routeConstant.evaluationRecordDetail.path
            .replace(":id", String(parentId.current))
            .replace(":org", org ? org : ""),
          { replace: true }
        );
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    handleGetQuestions();
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    dispatch(
      setBreadCrumb({
        crumbs: [
          {
            title: routeConstant.editEvaluationRecord.title,
          },
        ],
      })
    );
    handleGetEvaluations();
    // eslint-disable-next-line
  }, [id]);

  return (
    <section
      className={classNames(
        "relative h-full flex flex-col justify-between gap-4"
      )}
    >
      <div className={classNames(styles.mainContainer)}>
        {step === 1 ? (
          <QuestionnaireStep1
            formikRef={formikRef}
            formData={formData}
            isEdit
            evalsLoading={evalsLoading}
            initialFormData={initialFormData.current}
          />
        ) : step === 2 ? (
          <QuestionnaireStep2
            formikRef={formikRef}
            formData={formData}
            setStep={setStep}
            setLoading={setLoading}
            setFormData={setFormData}
          />
        ) : step === 3 ? (
          <QuestionnaireStep3 formikRef={formikRef} formData={formData} />
        ) : step === 4 ? (
          <QuestionnaireStep4 formikRef={formikRef} formData={formData} />
        ) : step === 5 ? (
          <QuestionnaireStep5 formikRef={formikRef} formData={formData} />
        ) : step === 6 ? (
          <QuestionnaireStep6 formikRef={formikRef} formData={formData} />
        ) : step === 7 ? (
          <QuestionnaireStep7 formikRef={formikRef} formData={formData} />
        ) : step === 8 ? (
          <QuestionnaireStep8 formikRef={formikRef} formData={formData} />
        ) : step === 9 ? (
          <QuestionnaireStep9
            formikRef={formikRef}
            formData={formData}
            setLoading={setLoading}
          />
        ) : step === 10 ? (
          <QuestionnaireStep10 formikRef={formikRef} formData={formData} />
        ) : step === 11 ? (
          <ReviewStep
            formData={formData}
            setStep={setStep}
            finalPayload={finalPayload}
            setLoading={setLoading}
          />
        ) : null}
      </div>
      <div className={classNames(styles.actionsContainer)}>
        {step > 1 ? (
          <CustomButton
            Icon={Icons.ArrowLeft}
            IconDirction="left"
            containerStyle={classNames(styles.prevButton)}
            onClick={() => {
              if (step > 1) {
                let finalArr = mergeArrays(
                  step,
                  formData,
                  formikRef.current.values?.jobs
                );
                setFormData(finalArr);
                setStep(step - 1);
              }
            }}
            disabled={step === 1 || loading || evalsLoading}
          />
        ) : null}
        <div className={classNames("w-full flex items-center justify-center")}>
          <CustomPagination
            currentPage={step}
            totalCount={10}
            pageSize={1}
            displayArrow={false}
            disabled={loading}
            onPageChange={(page: any) => {
              if (!loading && !evalsLoading) {
                if (page < step) {
                  let finalArr = mergeArrays(
                    step,
                    formData,
                    formikRef.current.values?.jobs
                  );
                  setFormData(finalArr);
                  setStep(page);
                } else {
                  formikRef.current.submitForm();
                  //immediate next page
                  setTimeout(() => {
                    if (
                      formikRef.current?.errors &&
                      // eslint-disable-next-line
                      Object.keys(formikRef.current.errors).length == 0 &&
                      !loadingRef.current
                    ) {
                      if (
                        checkIfWarning(formikRef.current.values?.jobs, step)
                      ) {
                        let finalArr = mergeArrays(
                          step,
                          formData,
                          formikRef.current.values?.jobs
                        );
                        if (
                          checkDropdownOptions(finalArr) ||
                          page === step + 1
                        ) {
                          setFormData(finalArr);
                          setStep(page);
                        }
                      } else {
                        handleShowConfirmationModal();
                      }
                    }
                  }, 200);
                }
              }
            }}
          />
        </div>
        <div className={classNames("flex items-center gap-2")}>
          <CustomButton
            title={step === 10 ? "Review" : step === 11 ? "Save" : "Continue"}
            Icon={step !== 11 ? Icons.ArrowRight : null}
            IconDirction="right"
            onClick={() => {
              if (step === 11) {
                handleSubmission();
              } else {
                formikRef.current.submitForm();
                setTimeout(() => {
                  if (
                    formikRef.current?.errors &&
                    // eslint-disable-next-line
                    Object.keys(formikRef.current.errors).length == 0 &&
                    !loadingRef.current
                  ) {
                    if (checkIfWarning(formikRef.current.values?.jobs, step)) {
                      let finalArr = mergeArrays(
                        step,
                        formData,
                        formikRef.current.values?.jobs
                      );
                      setFormData(finalArr);
                      if (step < 11) {
                        setStep(step + 1);
                      }
                    } else {
                      handleShowConfirmationModal();
                    }
                  }
                }, 200);
              }
            }}
            containerStyle={classNames(styles.nextButton)}
            disabled={loading || evalsLoading}
            loading={loading}
          />
        </div>
      </div>
      <ConfirmationModal
        show={showConfirmationModal}
        handleClose={handleCloseConfirmationModal}
        actionButtonText="Yes, Continue"
        cancelButtonText="No, Cancel"
        title="Improbable combination found!"
        subtitle="Are you sure you want to proceed with this improbable combination?"
        handleSubmit={() => {
          let finalArr = mergeArrays(
            step,
            formData,
            formikRef.current.values?.jobs
          );
          setFormData(finalArr);
          if (step < 11) {
            setStep(step + 1);
          }
          handleCloseConfirmationModal();
        }}
      />
    </section>
  );
};

export default EditQuestionnaire;
